import { useEffect, useRef, useCallback, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LayoutGrid, SquarePlus } from 'lucide-react'
import { useWorkspace } from './useWorkspace'
import { useWorkspaceManager } from './WorkspaceManagerContext'
import { WorkspaceMenuBar } from './WorkspaceMenuBar'
import { workspaceApi } from '../client/index.js'
import { useNewProject } from '@papan/files/ui'

interface EmptyWorkspaceViewProps {
  tenantId: string
  isOrg?: boolean
}

export function EmptyWorkspaceView({ isOrg }: EmptyWorkspaceViewProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const ws = useWorkspace({ folderId: null })
  const wsManager = useWorkspaceManager()
  const newProject = useNewProject()

  const navigateToProject = useCallback((folderId: string) => {
    const prefix = location.pathname.match(/^(\/~|\/t\/[^/]+)/)?.[1] || '/~'
    navigate(`${prefix}/workspace/${folderId}`, { replace: true })
  }, [navigate, location.pathname])
  const openNewProject = useCallback(() => {
    newProject.open({ onDone: navigateToProject })
  }, [newProject, navigateToProject])
  const openNewEntity = useCallback((entityKind: 'project' | 'miniapp') => {
    newProject.open({ entityKind, onDone: navigateToProject })
  }, [newProject, navigateToProject])

  const doNavigate = useCallback((folderId: string) => {
    wsManager.openWorkspace({ id: ws.workspaceId || folderId, folderId, name: ws.workspaceName || 'Loading...' })
    const prefix = location.pathname.match(/^(\/~|\/t\/[^/]+)/)?.[1] || '/~'
    navigate(`${prefix}/workspace/${folderId}`, { replace: true })
  }, [wsManager, ws.workspaceId, ws.workspaceName, navigate, location.pathname])

  // When workspace is created, navigate to it
  useEffect(() => {
    if (ws.folderId) doNavigate(ws.folderId)
  }, [ws.folderId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Permission check for org tenants
  const [canIndividual, setCanIndividual] = useState<boolean | null>(null)
  const [canOrganization, setCanOrganization] = useState<boolean | null>(null)
  useEffect(() => {
    if (!isOrg) return
    workspaceApi.checkNewWorkspacePermission('individual').then(r => setCanIndividual(r.canCreate)).catch(() => setCanIndividual(false))
    workspaceApi.checkNewWorkspacePermission('organization').then(r => setCanOrganization(r.canCreate)).catch(() => setCanOrganization(false))
  }, [isOrg])

  // Personal tenant: auto-create immediately (no permission split)
  const creatingRef = useRef(false)
  useEffect(() => {
    if (isOrg || ws.folderId || creatingRef.current) return
    creatingRef.current = true
    ws.ensureWorkspace().catch(() => { creatingRef.current = false })
  }, [isOrg]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    const prefix = location.pathname.match(/^(\/~|\/t\/[^/]+)/)?.[1] || '/~'
    navigate(`${prefix}/folder`)
  }, [navigate, location.pathname])

  const handleOpenRecentWorkspace = useCallback((wsFolderId: string) => {
    const prefix = location.pathname.match(/^(\/~|\/t\/[^/]+)/)?.[1] || '/~'
    navigate(`${prefix}/workspace/${wsFolderId}`)
  }, [navigate, location.pathname])

  // Load recent workspaces for the menu
  const [recentWorkspaces, setRecentWorkspaces] = useState<Array<{ folderId: string; name: string; path: string }>>([])
  const [_showWorkspacePicker, setShowWorkspacePicker] = useState(false)
  useEffect(() => {
    workspaceApi.listRecent().then(items => {
      setRecentWorkspaces(items.map(w => ({ folderId: w.folderId, name: w.name, path: w.path })))
    }).catch(() => {})
  }, [])

  const menuBar = (
    <WorkspaceMenuBar
      workspaceName="New Project"
      onClose={handleClose}
      editContext="explorer"
      onNewProject={() => openNewEntity('project')}
      onNewMiniapp={() => openNewEntity('miniapp')}
      onOpenWorkspace={() => setShowWorkspacePicker(true)}
      recentWorkspaces={recentWorkspaces}
      onOpenRecentWorkspace={handleOpenRecentWorkspace}
    />
  )

  // Personal: show loading while auto-creating
  if (!isOrg) {
    return (
      <div className="flex-1 flex flex-col themed-bg-primary">
        {menuBar}
        <div className="flex-1 flex items-center justify-center themed-text-muted">
          <div className="text-center">
            <LayoutGrid size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-sm">Creating workspace...</p>
          </div>
        </div>
      </div>
    )
  }

  // Org: show a single New Project entry — the audience dialog subsumes the old
  // My Space / Organization scope choice (§3.4).
  const loaded = canIndividual !== null && canOrganization !== null

  return (
    <div className="flex-1 flex flex-col themed-bg-primary">
      {menuBar}
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-6">
          <LayoutGrid size={48} className="mx-auto opacity-30 themed-text-muted" />
          <div>
            <p className="text-lg themed-text">New Project</p>
            <p className="text-sm themed-text-muted mt-1">Choose who can access it when you create</p>
          </div>
          <button
            disabled={!loaded || (!canIndividual && !canOrganization)}
            onClick={openNewProject}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg border transition-colors ${
              !loaded || (!canIndividual && !canOrganization)
                ? 'themed-border opacity-50 cursor-not-allowed'
                : 'themed-border hover:border-purple-500 hover:themed-bg-secondary cursor-pointer'
            }`}
          >
            <SquarePlus size={20} className={!loaded || (!canIndividual && !canOrganization) ? 'themed-text-muted' : 'text-purple-500'} />
            <span className="text-sm font-medium themed-text">New Project</span>
          </button>
        </div>
      </div>
    </div>
  )
}
